import ExcelJS from 'exceljs';
import { buildCsvFromRows, forceCsvText } from './csvExport';
import { downloadBytes, encodeUtf8Csv } from './downloadFile';
import { DE_EXCEL, fillSolid, thinBorder } from './reconciliation/excel/theme';
import { writeWorkbook } from './reconciliation/excel/v2/validator';

export const SECURITY_SIGNAL_HEADERS = [
  'Date',
  'Time (IST)',
  'Signal ID',
  'Event Type',
  'Severity',
  'Actor ID',
  'Actor Role',
  'Entity Type',
  'Entity ID',
  'Endpoint',
  'IP Address',
  'Reason / Detail',
  'Hit Count',
  'Acknowledged',
  'Acknowledged By',
  'Acknowledged At',
  'Additional Notes',
];

const COLUMN_WIDTHS = [14, 18, 10, 34, 12, 38, 12, 18, 38, 28, 18, 24, 10, 14, 38, 24, 48];

function getSignalId(row) {
  return String(row?.id ?? row?.metadata?.signalId ?? '');
}

export function getSecuritySignalSeverity(row) {
  return row?.metadata?.severity || (String(row?.action || '').includes('retry_burst') ? 'HIGH' : 'MEDIUM');
}

function formatEventLabel(action) {
  if (!action) return '';
  return String(action)
    .replace(/^security\./, '')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatDateParts(iso) {
  if (!iso) return { date: '', time: '', dateObj: null };
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: String(iso), time: '', dateObj: null };
    return {
      date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: d.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      }),
      dateObj: d,
    };
  } catch {
    return { date: String(iso), time: '', dateObj: null };
  }
}

function buildAdditionalNotes(meta = {}) {
  const {
    endpoint,
    ip,
    reason,
    severity,
    signalId,
    count,
    eventType,
    identifier,
    role,
    userAgent,
    windowMs,
    ...rest
  } = meta;
  const parts = [];
  if (eventType) parts.push(`eventType=${eventType}`);
  if (identifier) parts.push(`identifier=${identifier}`);
  if (role) parts.push(`role=${role}`);
  if (windowMs != null) parts.push(`windowMs=${windowMs}`);
  if (userAgent) parts.push(`userAgent=${userAgent}`);
  if (Object.keys(rest).length > 0) {
    try {
      parts.push(JSON.stringify(rest));
    } catch {
      parts.push(String(rest));
    }
  }
  return parts.join(' | ');
}

function rowToValues(row, ackMap = {}) {
  const signalId = getSignalId(row);
  const meta = row?.metadata || {};
  const ack = ackMap[signalId];
  const created = formatDateParts(row.created_at);
  const ackAt = formatDateParts(ack?.acknowledgedAt);

  return {
    date: created.date,
    time: created.time,
    dateObj: created.dateObj,
    signalId,
    eventType: row.action || '',
    eventLabel: formatEventLabel(row.action),
    severity: getSecuritySignalSeverity(row),
    actorId: row.actor_id || '',
    actorRole: row.actor_role || '',
    entityType: row.entity_type || '',
    entityId: row.entity_id || '',
    endpoint: meta.endpoint || '',
    ip: meta.ip || '',
    reason: meta.reason || meta.eventType || meta.identifier || '',
    hitCount: meta.count != null && meta.count !== '' ? String(meta.count) : '',
    acknowledged: ack?.acknowledged ? 'Yes' : 'No',
    ackBy: ack?.acknowledged ? (ack.actorId || '') : '',
    ackAt: ack?.acknowledged ? `${ackAt.date} ${ackAt.time}`.trim() : '',
    notes: buildAdditionalNotes(meta),
  };
}

function rowToCsvCells(row, ackMap = {}) {
  const v = rowToValues(row, ackMap);
  return [
    v.date,
    forceCsvText(v.time),
    forceCsvText(v.signalId),
    v.eventLabel || v.eventType,
    v.severity,
    forceCsvText(v.actorId),
    v.actorRole,
    v.entityType,
    forceCsvText(v.entityId),
    v.endpoint,
    forceCsvText(v.ip),
    v.reason,
    v.hitCount,
    v.acknowledged,
    v.acknowledged === 'Yes' ? forceCsvText(v.ackBy) : '',
    v.ackAt ? forceCsvText(v.ackAt) : '',
    v.notes,
  ];
}

export function buildSecuritySignalsCsv(rows, ackMap = {}) {
  const dataRows = rows.map((row) => rowToCsvCells(row, ackMap));
  return buildCsvFromRows(SECURITY_SIGNAL_HEADERS, dataRows);
}

export function buildSecuritySignalsFilename({ severityFilter = 'ALL', format = 'xlsx' } = {}) {
  const stamp = new Date().toISOString().slice(0, 10);
  const filter = severityFilter === 'ALL' ? 'All' : severityFilter;
  const ext = format === 'csv' ? 'csv' : 'xlsx';
  return `DE-Security-Signals_${filter}_${stamp}.${ext}`;
}

function styleHeaderCell(cell) {
  cell.font = { name: DE_EXCEL.fonts.body, size: 10, bold: true, color: { argb: DE_EXCEL.colors.white } };
  cell.fill = fillSolid(DE_EXCEL.colors.headerBg);
  cell.border = thinBorder(DE_EXCEL.colors.borderDark);
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
}

function styleDataCell(cell, { zebra = false, text = false } = {}) {
  cell.font = { name: DE_EXCEL.fonts.body, size: 10, color: { argb: 'FF1E293B' } };
  cell.fill = fillSolid(zebra ? DE_EXCEL.colors.zebra : DE_EXCEL.colors.white);
  cell.border = thinBorder(DE_EXCEL.colors.border);
  cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: false };
  if (text) cell.numFmt = '@';
}

function styleSeverityCell(cell, severity, zebra) {
  styleDataCell(cell, { zebra });
  if (severity === 'HIGH') {
    cell.font = { name: DE_EXCEL.fonts.body, size: 10, bold: true, color: { argb: DE_EXCEL.colors.debitText } };
    cell.fill = fillSolid(DE_EXCEL.colors.debitBg);
  } else if (severity === 'MEDIUM') {
    cell.font = { name: DE_EXCEL.fonts.body, size: 10, bold: true, color: { argb: 'FF92400E' } };
    cell.fill = fillSolid(DE_EXCEL.colors.warningBg);
  }
}

async function buildSecuritySignalsWorkbook(rows, ackMap = {}, options = {}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = DE_EXCEL.company;
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Security Signals', {
    views: [{ state: 'frozen', ySplit: 5, xSplit: 0, showGridLines: true }],
    properties: { defaultRowHeight: 18 },
  });

  SECURITY_SIGNAL_HEADERS.forEach((_, idx) => {
    sheet.getColumn(idx + 1).width = COLUMN_WIDTHS[idx] || 16;
  });

  sheet.mergeCells(1, 1, 1, SECURITY_SIGNAL_HEADERS.length);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = `${DE_EXCEL.company} — Security Signals Report`;
  titleCell.font = { name: DE_EXCEL.fonts.title, size: 14, bold: true, color: { argb: DE_EXCEL.colors.gold } };
  titleCell.fill = fillSolid(DE_EXCEL.colors.headerBg);
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  sheet.getRow(1).height = 26;

  const filterLabel = options.severityFilter === 'ALL' ? 'All severities' : `${options.severityFilter} only`;
  const generatedLabel = new Date().toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  sheet.mergeCells(2, 1, 2, SECURITY_SIGNAL_HEADERS.length);
  const metaCell = sheet.getCell(2, 1);
  metaCell.value = `Generated: ${generatedLabel}   |   Filter: ${filterLabel}   |   Total signals: ${rows.length}`;
  metaCell.font = { name: DE_EXCEL.fonts.body, size: 10, color: { argb: DE_EXCEL.colors.muted } };
  metaCell.fill = fillSolid(DE_EXCEL.colors.zebra);
  metaCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  sheet.getRow(2).height = 20;

  sheet.getRow(3).height = 6;
  sheet.getRow(4).height = 6;

  const headerRow = sheet.getRow(5);
  SECURITY_SIGNAL_HEADERS.forEach((label, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = label;
    styleHeaderCell(cell);
  });
  headerRow.height = 22;

  rows.forEach((row, rowIdx) => {
    const v = rowToValues(row, ackMap);
    const excelRow = sheet.getRow(6 + rowIdx);
    const zebra = rowIdx % 2 === 1;
    const values = [
      v.date,
      v.time,
      v.signalId,
      v.eventLabel || v.eventType,
      v.severity,
      v.actorId,
      v.actorRole,
      v.entityType,
      v.entityId,
      v.endpoint,
      v.ip,
      v.reason,
      v.hitCount,
      v.acknowledged,
      v.ackBy,
      v.ackAt,
      v.notes,
    ];

    values.forEach((value, colIdx) => {
      const cell = excelRow.getCell(colIdx + 1);
      cell.value = value ?? '';
      if (colIdx === 4) {
        styleSeverityCell(cell, v.severity, zebra);
      } else if ([2, 5, 8, 10, 14, 15].includes(colIdx)) {
        styleDataCell(cell, { zebra, text: true });
      } else {
        styleDataCell(cell, { zebra });
      }
    });
    excelRow.height = 18;
  });

  if (rows.length > 0) {
    sheet.autoFilter = {
      from: { row: 5, column: 1 },
      to: { row: 5 + rows.length, column: SECURITY_SIGNAL_HEADERS.length },
    };
  }

  const { buffer } = await writeWorkbook(workbook);
  return buffer;
}

export async function exportSecuritySignalsReport(rows, ackMap = {}, options = {}) {
  if (!rows.length) return { ok: false, rowCount: 0, filename: '' };

  const filename = buildSecuritySignalsFilename({ ...options, format: 'xlsx' });
  const buffer = await buildSecuritySignalsWorkbook(rows, ackMap, options);
  await downloadBytes(
    buffer,
    filename,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
  return { ok: true, rowCount: rows.length, filename };
}

export async function exportSecuritySignalsCsvReport(rows, ackMap = {}, options = {}) {
  if (!rows.length) return { ok: false, rowCount: 0, filename: '' };
  const csv = buildSecuritySignalsCsv(rows, ackMap);
  const filename = buildSecuritySignalsFilename({ ...options, format: 'csv' });
  const bytes = encodeUtf8Csv(csv);
  await downloadBytes(bytes, filename, 'text/csv;charset=utf-8');
  return { ok: true, rowCount: rows.length, filename };
}
